import { createTheme, type ThemeOptions } from "@mui/material/styles"
import {
  colors,
  typography,
  spacing,
  breakpoints,
  getViraPalette,
  type ViraPalette,
  type ViraThemeMode,
} from "./tokens"

declare module "@mui/material/styles" {
  interface Palette {
    vira: ViraPalette
  }
  interface PaletteOptions {
    vira?: ViraPalette
  }
}

export type { ViraThemeMode }

export type CreateViraThemeOptions = ThemeOptions & {
  /** Color mode. Defaults to `light` so existing callers stay unchanged. */
  mode?: ViraThemeMode
}

export function createViraTheme({
  mode = "light",
  ...overrides
}: CreateViraThemeOptions = {}) {
  const vira = getViraPalette(mode)
  const isDark = mode === "dark"

  const base = createTheme({
    spacing: spacing.unit,
    breakpoints: {
      values: { ...breakpoints },
    },
    typography: {
      fontFamily: typography.fontFamily.body,
      fontSize: 16,
      h1: {
        fontFamily: typography.fontFamily.display,
        fontWeight: 700,
        fontSize: 34,
        letterSpacing: typography.letterSpacing,
        color: isDark ? vira.ink : colors.neutral.denim,
      },
      h2: {
        fontFamily: typography.fontFamily.display,
        fontWeight: 600,
        fontSize: 30,
        letterSpacing: typography.letterSpacing,
        color: vira.ink,
      },
      h3: {
        fontFamily: typography.fontFamily.display,
        fontWeight: 600,
        fontSize: 24,
        letterSpacing: typography.letterSpacing,
        color: vira.ink,
      },
      h4: {
        fontFamily: typography.fontFamily.display,
        fontWeight: 600,
        fontSize: 20,
        letterSpacing: typography.letterSpacing,
        color: vira.ink,
      },
      h5: {
        fontFamily: typography.fontFamily.display,
        fontWeight: 600,
        fontSize: 18,
        letterSpacing: typography.letterSpacing,
        color: vira.ink,
      },
      h6: {
        fontFamily: typography.fontFamily.body,
        fontWeight: 700,
        fontSize: 16,
        letterSpacing: typography.letterSpacing,
        color: vira.ink,
      },
      body1: {
        fontFamily: typography.fontFamily.body,
        fontWeight: 400,
        fontSize: 16,
        letterSpacing: typography.letterSpacing,
        color: vira.ink,
      },
      body2: {
        fontFamily: typography.fontFamily.body,
        fontWeight: 400,
        fontSize: 14,
        letterSpacing: typography.letterSpacing,
        color: vira.muted,
      },
      button: {
        fontFamily: typography.fontFamily.body,
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: 0.26,
        textTransform: "none",
      },
      caption: {
        fontFamily: typography.fontFamily.body,
        fontWeight: 400,
        fontSize: 12,
        letterSpacing: typography.letterSpacing,
        color: vira.subtle,
      },
      overline: {
        fontFamily: typography.fontFamily.body,
        fontWeight: 600,
        fontSize: 11.5,
        letterSpacing: 1.5,
        textTransform: "uppercase",
        color: vira.muted,
      },
    },
    palette: {
      mode,
      primary: {
        main: isDark ? colors.primary.periwinkle : colors.primary.midnight,
        light: isDark ? colors.primary.ice : colors.primary.periwinkle,
        dark: isDark ? colors.primary.shadow : vira.primaryBtn,
        contrastText: isDark ? colors.neutral.space : colors.neutral.white,
      },
      secondary: {
        main: isDark ? colors.secondary.rose : colors.secondary.grapefruit,
        contrastText: isDark ? colors.neutral.space : colors.neutral.white,
      },
      error: {
        main: colors.secondary.crimson,
      },
      background: {
        default: vira.pageBg,
        paper: isDark ? colors.neutral.denim : colors.neutral.white,
      },
      text: {
        primary: vira.ink,
        secondary: vira.muted,
        disabled: vira.disabledText,
      },
      divider: vira.border,
      action: {
        hover: isDark ? "rgba(117, 145, 228, 0.08)" : "rgba(47, 59, 90, 0.04)",
        selected: vira.navActiveBg,
        disabled: vira.disabledText,
        disabledBackground: vira.lavender,
      },
      vira,
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 7,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          },
          containedPrimary: {
            backgroundColor: vira.primaryBtn,
            color: isDark ? colors.neutral.space : colors.neutral.white,
            "&:hover": {
              backgroundColor: isDark
                ? colors.primary.ice
                : colors.primary.midnight,
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: { height: "100%" },
          body: {
            height: "100%",
            backgroundColor: vira.canvas,
            color: vira.ink,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
    },
  })

  return createTheme(base, overrides)
}

export type ViraTheme = ReturnType<typeof createViraTheme>
